import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface Notification {
  id: string;
  company_id: string;
  notification_type: string;
  title: string;
  message: string;
  reference_id: string | null;
  read: boolean;
  created_at: string;
}

export function useInvestorNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  async function loadNotifications() {
    if (!user) return;

    try {
      const { data: notifs } = await supabase
        .from('investor_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      const unreadNotifications = notifs?.filter((n) => !n.read) || [];

      setNotifications(notifs || []);
      setUnreadCount(unreadNotifications.length);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  }

  async function markAsRead(notificationId: string) {
    if (!user) return;

    try {
      await supabase
        .from('investor_notifications')
        .update({ read: true })
        .eq('id', notificationId)
        .eq('user_id', user.id);

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }

  async function markAllAsRead() {
    if (!user) return;

    try {
      await supabase
        .from('investor_notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  }

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  // Placeholder effect to maintain hook count (realtime disabled)
  useEffect(() => {
    // Realtime updates disabled - notifications only refresh on page load or manual refresh
    return () => {};
  }, [user]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refresh: loadNotifications,
  };
}
